import { useNavigate } from "@tanstack/react-router";
import { ClipboardList, Loader2, MessageCircleQuestion, Plus, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CreateAuditDialog } from "@/components/audits/CreateAuditDialog";
import { RequestGuidanceDialog } from "@/components/audits/RequestGuidanceDialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { ResizableTh } from "@/components/shared/resizable-th";
import { Button } from "@/components/ui/button";
import { useAudits } from "@/hooks/use-audits";
import type { Project } from "@/hooks/use-projects";
import { useAllProjects } from "@/hooks/use-projects";
import type { ResizableColumn } from "@/hooks/use-resizable-columns";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import { ProjectTeamDialog } from "./ProjectTeamDialog";

const PIPELINE_SIZE = 5;

const HEADERS = ["Project Lead", "Demand", "Demand End Date", "Product"];

const COLUMNS: ResizableColumn[] = [
  { key: "projectLead", defaultWidth: 160 },
  { key: "demand", defaultWidth: 280 },
  { key: "demandEndDate", defaultWidth: 130 },
  { key: "product", defaultWidth: 120 },
  { key: "team", defaultWidth: 70, minWidth: 60 },
  { key: "actions", defaultWidth: 340, minWidth: 300 },
];

function PipelineRow({
  project,
  onCreateAudit,
  onRequestGuidance,
  onShowTeam,
}: {
  project: Project;
  onCreateAudit: (projectId: string) => void;
  onRequestGuidance: (projectId: string) => void;
  onShowTeam: (project: Project) => void;
}) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const demandEndDate = project.demandEndDate
    ? new Date(project.demandEndDate).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    : null;

  return (
    <tr
      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
      onClick={() =>
        navigate({ to: "/dashboard/projects/$projectId", params: { projectId: project.id } })
      }
    >
      <td className="px-4 py-3 text-muted-foreground" title={project.projectLead ?? undefined}>
        {project.projectLead ?? "—"}
      </td>
      <td className="px-4 py-3 text-muted-foreground" title={project.demand ?? undefined}>
        {project.demand ?? "—"}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{demandEndDate ?? "—"}</td>
      <td className="px-4 py-3 text-muted-foreground" title={project.product ?? undefined}>
        {project.product ?? "—"}
      </td>
      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" onClick={() => onShowTeam(project)}>
          <Users className="h-4 w-4" />
          <span className="sr-only">View team</span>
        </Button>
      </td>
      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => onRequestGuidance(project.id)}>
            <MessageCircleQuestion className="h-4 w-4 mr-1" />
            Request Guidance
          </Button>
          <Button
            size="sm"
            disabled={isLoading}
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => onCreateAudit(project.id), 0);
            }}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Plus className="h-4 w-4 mr-1" />
                New Audit
              </>
            )}
          </Button>
        </div>
      </td>
    </tr>
  );
}

export function ProjectPipelineTable() {
  const { data: projects, isLoading: projectsLoading, isError, refetch } = useAllProjects();
  const { data: audits, isLoading: auditsLoading } = useAudits();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>();
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [guidanceProjectId, setGuidanceProjectId] = useState<string | undefined>();
  const [teamDialogProject, setTeamDialogProject] = useState<Project | null>(null);

  const isLoading = projectsLoading || auditsLoading;

  const { widths, startResize } = useResizableColumns("project-pipeline", COLUMNS);

  const pipelineProjects = useMemo(() => {
    if (!projects) return [];
    const currentYear = new Date().getFullYear();
    const projectIdsWithAudits = new Set(
      (audits ?? []).filter((a) => a.projectId !== null).map((a) => a.projectId as string),
    );
    return projects
      .filter((p) => !projectIdsWithAudits.has(p.id))
      .filter((p) => p.complianceRate !== 1)
      .filter((p) => p.demandEndDate !== null)
      .filter((p) => new Date(p.demandEndDate as string).getFullYear() === currentYear)
      .sort(
        (a, b) =>
          new Date(a.demandEndDate as string).getTime() -
          new Date(b.demandEndDate as string).getTime(),
      )
      .slice(0, PIPELINE_SIZE);
  }, [projects, audits]);

  const handleCreateAudit = (projectId: string) => {
    setSelectedProjectId(projectId);
    setCreateOpen(true);
  };

  const handleRequestGuidance = (projectId: string) => {
    setGuidanceProjectId(projectId);
    setGuidanceOpen(true);
  };

  if (isError) {
    return <ErrorState message="Failed to load project pipeline" onRetry={() => refetch()} />;
  }

  return (
    <div className="rounded-lg border border-border p-6 space-y-4">
      <h2 className="text-lg font-semibold">Project Pipeline</h2>

      {isLoading ? (
        <div className="rounded-md border border-border overflow-x-auto contain-layout">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                {[...HEADERS, "Team", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {["sk-1", "sk-2", "sk-3"].map((rk) => (
                <tr key={rk} className="border-b border-border last:border-0">
                  {["c1", "c2", "c3", "c4", "c5", "c6"].map((ck) => (
                    <td key={ck} className="px-4 py-3">
                      <div className="h-4 bg-muted rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : pipelineProjects.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="No projects need attention"
          description="Every upcoming project either has an audit assigned or is already fully compliant."
        />
      ) : (
        <div className="rounded-md border border-border overflow-x-auto contain-layout">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <ResizableTh width={widths.projectLead} onResizeStart={startResize("projectLead")}>
                  Project Lead
                </ResizableTh>
                <ResizableTh width={widths.demand} onResizeStart={startResize("demand")}>
                  Demand
                </ResizableTh>
                <ResizableTh
                  width={widths.demandEndDate}
                  onResizeStart={startResize("demandEndDate")}
                >
                  Demand End Date
                </ResizableTh>
                <ResizableTh width={widths.product} onResizeStart={startResize("product")}>
                  Product
                </ResizableTh>
                <ResizableTh
                  width={widths.team}
                  onResizeStart={startResize("team")}
                  className="text-center"
                >
                  Team
                </ResizableTh>
                <ResizableTh width={widths.actions} onResizeStart={startResize("actions")} />
              </tr>
            </thead>
            <tbody>
              {pipelineProjects.map((project) => (
                <PipelineRow
                  key={project.id}
                  project={project}
                  onCreateAudit={handleCreateAudit}
                  onRequestGuidance={handleRequestGuidance}
                  onShowTeam={setTeamDialogProject}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateAuditDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultProjectId={selectedProjectId}
      />
      <RequestGuidanceDialog
        open={guidanceOpen}
        onOpenChange={setGuidanceOpen}
        projectId={guidanceProjectId}
      />
      {teamDialogProject && (
        <ProjectTeamDialog
          open
          onOpenChange={(open) => !open && setTeamDialogProject(null)}
          project={teamDialogProject}
        />
      )}
    </div>
  );
}
